// Third Party
import * as React from 'react';
import Image from 'next/image';
import { StaticImageData } from 'next/image';
import { useMergeRefs } from 'use-callback-ref';

// Utils
import { WithCommonProps } from '@types';
import { useAnalytics } from '@contexts/use-analytics';

// UI
import GoogleLogo from '../../../public/assets/svg/social-logos/google.svg';
import FacebookLogo from '../../../public/assets/svg/social-logos/facebook.png';
import AppleLogo from '../../../public/assets/svg/social-logos/apple.svg';
import { Button, ButtonProps } from '@components/button';

export enum SocialLoginType {
  Google = 'Google',
  Facebook = 'Facebook',
  Apple = 'Apple',
}

const socialLogins: {
  value: SocialLoginType;
  logo: StaticImageData;
  text: string;
}[] = [
  {
    value: SocialLoginType.Google,
    logo: GoogleLogo,
    text: 'Continue with Google',
  },
  {
    value: SocialLoginType.Facebook,
    logo: FacebookLogo,
    text: 'Continue with Facebook',
  },
  {
    value: SocialLoginType.Apple,
    logo: AppleLogo,
    text: 'Continue with Apple',
  },
];

export interface SocialLoginProps extends WithCommonProps<
  Omit<ButtonProps, 'variant'>
> {
  /** Name of the login to be displayed */
  social: SocialLoginType;
  /** Whether this is being used in a signup context (defaults to true) */
  isSignUp?: boolean;
}

export const SocialLogin = React.forwardRef(
  (
    props: SocialLoginProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const {
      children,
      social,
      isSignUp = true,
      onClick,
      color,
      ...rest
    } = props;
    const newRef = React.useRef<HTMLButtonElement>(null);
    const buttonRef = useMergeRefs<HTMLButtonElement>([newRef, ref]);
    const { emitSignUpBeginEvent } = useAnalytics();

    // Find the social login entry that matches the socialType prop
    const socialLoginEntry = socialLogins.find(
      (entry) => entry.value === social
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isSignUp) {
        emitSignUpBeginEvent(social.toLowerCase());
      }
      onClick?.(event);
    };

    // Compute color: outlined buttons don't support 'secondary', so convert it to 'default'
    const computedColor = color === 'secondary' ? 'default' : color;

    if (!socialLoginEntry) {
      return <div />;
    } else {
      return (
        <>
          <Button
            variant="outlined"
            color={computedColor}
            {...rest}
            onClick={handleClick}
            ref={buttonRef}
            aria-label={socialLoginEntry.text}
            className="w-full bg-white hover:bg-off-white active:scale-95 active:bg-gray-xlight"
          >
            <div className="flex w-full items-center justify-between py-1">
              <div className="w-[20px]">
                <Image
                  width={20}
                  height={20}
                  src={socialLoginEntry.logo}
                  className="object-cover object-center"
                  alt={`${social} logo`}
                />
              </div>
              <p>{socialLoginEntry.text}</p>
              <div className="w-[20px]" />
            </div>
          </Button>
        </>
      );
    }
  }
);

SocialLogin.displayName = 'SocialLogin';
